<template>
  <DashboardLayout>
    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <router-link :to="{ name: 'ViewSpecialApprovalTypes' }" class="mb-0 main-identifier fw-semibold text-decoration-none"
        title="Go back to Special Approval Types">
        Special Approval Types
      </router-link>
      <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">View Special Approval Type</p>
    </div>

    <div class="d-flex justify-content-center mb-5">
      <div class="form-outer">
        <form @submit.prevent>
          <div class="crud-form position-relative">

            <div class="form-inner mb-2">
              <div class="form-header d-flex justify-content-between align-items-center">
                <h1 class="mb-0">Special Approval Type</h1>
              </div>

              <div class="memo-content-area position-relative">
                <div v-if="!isDataLoaded" class="loading-overlay text-center">
                  <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
                  <p class="mt-2 mb-0 text-muted">Loading special approval type details...</p>
                </div>

                <div v-if="isDataLoaded" class="form-body mt-3 mb-3">

                  <div class="form-row">
                    <div class="form-row-label">
                      <label class="form-label">Name:</label>
                    </div>
                    <div class="form-row-input">
                      <input type="text" class="form-control form-view-only-input" :value="form.name" placeholder="" readonly />
                    </div>
                  </div>

                  <div class="form-row">
                    <div class="form-row-label">
                      <label class="form-label">Description:</label>
                    </div>
                    <div class="form-row-input">
                      <input type="text" class="form-control form-view-only-input" :value="form.description" placeholder="" readonly />
                    </div>
                  </div>

                  <div class="form-row">
                    <div class="form-row-label">
                      <label class="form-label">Maximum Amount:</label>
                    </div>
                    <div class="form-row-input">
                      <input type="text" class="form-control form-view-only-input" :value="formatCurrency(form.maxAmount)" placeholder="" readonly />
                    </div>
                  </div>

                  <div class="form-row">
                    <div class="form-row-label">
                      <label class="form-label">Approval Type:</label>
                    </div>
                    <div class="form-row-input">
                      <input type="text" class="form-control form-view-only-input" :value="approvalTypeLabel" placeholder="" readonly />
                    </div>
                  </div>

                  <div class="form-row">
                    <div class="form-row-label">
                      <label class="form-label">Creation Date:</label>
                    </div>
                    <div class="form-row-input">
                      <input type="text" class="form-control form-view-only-input" :value="formatDate(form.createdDate)" placeholder="" readonly />
                    </div>
                  </div>

                </div>
              </div>
            </div>

          </div>
        </form>
      </div>
    </div>
  </DashboardLayout>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import axios from 'axios'
import Cookies from 'js-cookie'
import { useRoute } from 'vue-router'
import DashboardLayout from '../../layout/DashboardLayout.vue'

const root_api_url = process.env.VUE_APP_API_ROOT_URL
const route = useRoute()
const id = route.params.id

const isDataLoaded = ref(false)

const form = reactive({
  name: '',
  description: '',
  maxAmount: '',
  globalApprovalInd: 0,
  monthlyApprovalInd: 0,
  createdDate: '',
})

const approvalTypeLabel = computed(() => {
  if (form.globalApprovalInd) return 'Global Approval'
  if (form.monthlyApprovalInd) return 'Monthly Approval'
  return 'N/A'
})

const fetchSpecialApprovalType = async () => {
  const token = Cookies.get('auth_token')
  isDataLoaded.value = false
  try {
    const { data } = await axios.get(`${root_api_url}/appraisal/spec-approval-type/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    form.name = data.name
    form.description = data.description
    form.maxAmount = data.maxAmount
    form.globalApprovalInd = data.globalApprovalInd
    form.monthlyApprovalInd = data.monthlyApprovalInd
    form.createdDate = data.createdDate
  } catch (error) {
    console.error('Failed to fetch special approval type:', error)
  } finally {
    isDataLoaded.value = true
  }
}

const formatDate = (dateString) => {
  if (!dateString) return 'N/A'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const formatCurrency = (val) => {
  if (val === '' || val === null || val === undefined) return 'N/A'
  return '₦' + Number(parseFloat(val).toFixed(2)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

onMounted(() => {
  document.title = 'View Special Approval Type'
  fetchSpecialApprovalType()
})
</script>

<style scoped>
.memo-content-area {
  min-height: 280px;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 20;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  pointer-events: all;
}

.form-outer {
  width: 100%;
  max-width: 800px;
}

.crud-form {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.form-header h1 {
  font-size: 1.5rem;
  color: #333;
}

/* Form Row Layout */
.form-row {
  display: flex;
  margin-bottom: 1.5rem;
  gap: 1rem;
  align-items: flex-start;
}

.form-row-label {
  flex: 0 0 30%;
  text-align: right;
  padding-top: 0.5rem;
}

.form-row-label label {
  font-weight: 600;
    color: var(--color);
  margin-bottom: 0;
}

.form-row-input {
  flex: 1;
}

/* Tablet Responsiveness (768px - 1024px) */
@media screen and (max-width: 1024px) {
  .form-outer {
    max-width: 100%;
  }

  .form-row-label {
    flex: 0 0 40%;
  }
}

/* Mobile Responsiveness (below 768px) */
@media screen and (max-width: 768px) {
  .form-header h1 {
    font-size: 1.25rem;
  }

  .form-row {
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 1.25rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #e9ecef;
  }

  .form-row-label {
    flex: none;
    text-align: left;
    padding-top: 0;
  }

  .form-row-label label {
    font-size: 0.875rem;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .form-row-input {
    flex: none;
    width: 100%;
  }

  .page-identifier {
    font-size: 0.875rem;
  }

  .page-identifier .material-symbols-outlined {
    font-size: 1.25rem;
  }
}

/* Small Mobile (below 480px) */
@media screen and (max-width: 480px) {
  .form-header h1 {
    font-size: 1.125rem;
  }

  .form-row {
    margin-bottom: 1rem;
    padding-bottom: 0.75rem;
  }

  .form-row-label label {
    font-size: 0.8125rem;
  }
}

/* Print Styles */
@media print {
  .page-identifier {
    display: none !important;
  }

  .form-outer {
    max-width: 100%;
    box-shadow: none;
  }

  .form-row {
    page-break-inside: avoid;
  }
}
</style>
